import type { FC, FormEvent, ReactNode } from 'react'
import type { GradeType } from '@/types'

import { useState } from 'react'
import { compareText, formatDecimal } from '@/lib/academic'
import { DEMO_TODAY, formatDate } from '@/lib/dates'
import { printPage } from '@/lib/spreadsheet'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { SegmentedTabs } from '@/components/app/report-tabs'
import { AttendanceBadge } from '@/components/app/status'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { DEFAULT_KKM, GRADE_TYPE_LABELS, GRADE_TYPES } from '@/constants/academic'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import { ClipboardCheck, Eye, Printer, Trash2 } from 'lucide-react'

type ViewType = 'absen' | 'nilai' | 'kumpulan'

const VIEW_TABS: { key: ViewType, label: string }[] = [
    { key: 'absen', label: 'Absen' },
    { key: 'nilai', label: 'Nilai' },
    { key: 'kumpulan', label: 'Gabungan' }
]

type FilterFormProps = {
    viewType: ViewType
    dates: string[]
    date: string
    classId: string
    subjectId: string
    type: string
    onApply: (values: { tanggal: string, kelas: string, mapel: string, jenis: string }) => void
}

const FilterForm: FC<FilterFormProps> = ({ viewType, dates, date, classId, subjectId, type, onApply }): ReactNode => {
    const { classes, subjects } = useAcademic()
    const [values, setValues] = useState({ tanggal: date, kelas: classId, mapel: subjectId, jenis: type })

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        onApply(values)
    }

    return (
        <Card className='mb-6 p-4 sm:p-5 print:hidden'>
            <form onSubmit={handleSubmit} className='grid gap-4 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto] lg:items-end'>
                <Field label='Tanggal' htmlFor='reportDate'>
                    <Select id='reportDate' value={values.tanggal} onChange={event => setValues(current => ({ ...current, tanggal: event.target.value }))}>
                        {!dates.includes(date) && <option value={date}>{formatDate(date, 'short')} · tanggal pilihan</option>}
                        {dates.map(day => <option key={day} value={day}>{formatDate(day, 'short')}</option>)}
                    </Select>
                </Field>

                <Field label='Kelas' htmlFor='reportClass'>
                    <Select id='reportClass' value={values.kelas} onChange={event => setValues(current => ({ ...current, kelas: event.target.value }))}>
                        <option value=''>Semua kelas</option>
                        {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                </Field>

                {viewType !== 'absen' && (
                    <>
                        <Field label='Mata pelajaran' htmlFor='reportSubject'>
                            <Select id='reportSubject' value={values.mapel} onChange={event => setValues(current => ({ ...current, mapel: event.target.value }))}>
                                <option value=''>Semua mata pelajaran</option>
                                {subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </Select>
                        </Field>

                        <Field label='Jenis nilai' htmlFor='reportType'>
                            <Select id='reportType' value={values.jenis} onChange={event => setValues(current => ({ ...current, jenis: event.target.value }))}>
                                <option value='semua'>Semua jenis</option>
                                {GRADE_TYPES.map(item => <option key={item} value={item}>{GRADE_TYPE_LABELS[item]}</option>)}
                            </Select>
                        </Field>
                    </>
                )}

                <Button type='submit' className='lg:w-28'>
                    <Eye aria-hidden='true' />
                    Lihat
                </Button>
            </form>
        </Card>
    )
}

const DailyReportsPage: FC = (): ReactNode => {
    const { classes, subjects, attendance, grades, studentById, classById, subjectById, actions } = useAcademic()
    const { ready, get, set } = useQueryParams()
    const confirm = useConfirm()
    const toast = useToast()

    if (!ready) return <PageSkeleton />

    const requestedView = get('view')
    const viewType: ViewType = requestedView === 'nilai' || requestedView === 'kumpulan' ? requestedView : 'absen'

    const attendanceDates = viewType !== 'nilai' ? Array.from(new Set(attendance.map(record => record.date))).sort().reverse().slice(0, 60) : []
    const gradeDates = viewType !== 'absen' ? Array.from(new Set(grades.map(record => record.date))).sort().reverse().slice(0, 60) : []
    const dates = Array.from(new Set([...attendanceDates, ...gradeDates])).sort().reverse()

    const requestedDate = get('tanggal') ?? ''
    const date = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate : dates[0] ?? DEMO_TODAY
    const classId = classes.some(item => item.id === get('kelas')) ? get('kelas') as string : ''
    const subjectId = subjects.some(item => item.id === get('mapel')) ? get('mapel') as string : ''
    const requestedType = get('jenis') as GradeType | undefined
    const type = requestedType && GRADE_TYPES.includes(requestedType) ? requestedType : 'semua'

    const attendanceRows = attendance
        .filter(record => record.date === date && (!classId || record.classId === classId))
        .map(record => ({ record, student: studentById.get(record.studentId), className: classById.get(record.classId)?.name ?? '-' }))
        .filter(row => row.student)
        .sort((a, b) => compareText(a.className, b.className) || compareText(a.student?.name ?? '', b.student?.name ?? ''))

    const gradeRows = grades
        .filter(record => record.date === date && (!subjectId || record.subjectId === subjectId) && (type === 'semua' || record.type === type))
        .map(record => {
            const student = studentById.get(record.studentId)
            const subject = subjectById.get(record.subjectId)

            return {
                record,
                student,
                className: student ? classById.get(student.classId)?.name ?? '-' : '-',
                subjectName: subject?.name ?? '-',
                kkm: record.kkm ?? subject?.kkm ?? DEFAULT_KKM
            }
        })
        .filter(row => row.student && (!classId || row.student.classId === classId))
        .sort((a, b) => compareText(a.className, b.className) || compareText(a.student?.name ?? '', b.student?.name ?? '') || compareText(a.subjectName, b.subjectName))

    const tabHref = (key: ViewType): string => {
        const params = new URLSearchParams()

        params.set('view', key)
        if (get('tanggal')) params.set('tanggal', get('tanggal') as string)
        if (classId) params.set('kelas', classId)
        if (subjectId) params.set('mapel', subjectId)
        if (type !== 'semua') params.set('jenis', type)

        return `/laporan-harian?${params.toString()}`
    }

    const handleDeleteAttendance = async (id: string): Promise<void> => {
        if (!await confirm({ title: 'Hapus catatan absensi ini?', confirmLabel: 'Ya, hapus' })) return

        const result = actions.deleteAttendance(id)

        if (result.ok) toast.success(result.message ?? 'Catatan absensi dihapus.')
    }

    const handleDeleteGrade = async (id: string): Promise<void> => {
        if (!await confirm({ title: 'Hapus catatan nilai ini?', confirmLabel: 'Ya, hapus' })) return

        const result = actions.deleteGrade(id)

        if (result.ok) toast.success(result.message ?? 'Catatan nilai dihapus.')
    }

    return (
        <>
            <Seo title='Laporan harian' />

            <PageHeader
                eyebrow='Laporan harian'
                title='Preview Absen & Penilaian'
                description='Lihat catatan kehadiran dan nilai berdasarkan tanggal.'
                actions={(
                    <Button variant='outline' onClick={printPage}>
                        <Printer aria-hidden='true' />
                        Cetak / Simpan PDF
                    </Button>
                )}
            />

            <SegmentedTabs
                label='Jenis laporan'
                items={VIEW_TABS.map(tab => ({ href: tabHref(tab.key), label: tab.label, active: viewType === tab.key }))}
            />

            <FilterForm
                key={`${viewType}-${date}-${classId}-${subjectId}-${type}`}
                viewType={viewType}
                dates={dates}
                date={date}
                classId={classId}
                subjectId={subjectId}
                type={type}
                onApply={values => set({
                    view: viewType,
                    tanggal: values.tanggal,
                    kelas: values.kelas || null,
                    mapel: viewType !== 'absen' ? values.mapel || null : null,
                    jenis: viewType !== 'absen' && values.jenis !== 'semua' ? values.jenis : null
                })}
            />

            <div className='space-y-6'>
                {viewType !== 'nilai' && (
                    <Card>
                        <CardHeader
                            icon={<ClipboardCheck />}
                            title={`Catatan absensi · ${formatDate(date, 'long')}`}
                            action={<Badge tone='primary'>{attendanceRows.length} catatan</Badge>}
                        />

                        <Table>
                            <THead>
                                <tr>
                                    <TH>NIS</TH>
                                    <TH>Nama</TH>
                                    <TH>Kelas</TH>
                                    <TH>Status</TH>
                                    <TH className='text-right print:hidden'>Aksi</TH>
                                </tr>
                            </THead>

                            <TBody>
                                {attendanceRows.map(({ record, student, className }) => (
                                    <TR key={record.id}>
                                        <TD className='font-mono text-xs text-muted-foreground'>{student?.nis}</TD>
                                        <TD className='font-semibold text-foreground'>{student?.name}</TD>
                                        <TD>{className}</TD>
                                        <TD><AttendanceBadge status={record.status} /></TD>
                                        <TD className='text-right print:hidden'>
                                            <Button variant='danger-outline' size='xs' onClick={() => void handleDeleteAttendance(record.id)}>
                                                <Trash2 aria-hidden='true' />
                                                Hapus
                                            </Button>
                                        </TD>
                                    </TR>
                                ))}

                                {attendanceRows.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className='px-5 py-10 text-center text-sm text-muted-foreground'>
                                            Belum ada catatan absensi pada tanggal ini.
                                        </td>
                                    </tr>
                                )}
                            </TBody>
                        </Table>
                    </Card>
                )}

                {viewType !== 'absen' && (
                    <Card>
                        <CardHeader
                            title={`Catatan penilaian · ${formatDate(date, 'long')}`}
                            action={<Badge tone='primary'>{gradeRows.length} catatan</Badge>}
                        />

                        <Table>
                            <THead>
                                <tr>
                                    <TH>NIS</TH>
                                    <TH>Nama</TH>
                                    <TH>Kelas</TH>
                                    <TH>Mata pelajaran</TH>
                                    <TH>Jenis</TH>
                                    <TH className='text-center'>Nilai</TH>
                                    <TH className='text-center'>KKM</TH>
                                    <TH className='text-right print:hidden'>Aksi</TH>
                                </tr>
                            </THead>

                            <TBody>
                                {gradeRows.map(({ record, student, className, subjectName, kkm }) => (
                                    <TR key={record.id}>
                                        <TD className='font-mono text-xs text-muted-foreground'>{student?.nis}</TD>
                                        <TD className='font-semibold text-foreground'>{student?.name}</TD>
                                        <TD>{className}</TD>
                                        <TD>{subjectName}</TD>
                                        <TD>{GRADE_TYPE_LABELS[record.type]}</TD>
                                        <TD className='text-center'>
                                            <span className={record.score >= kkm ? 'font-bold text-success' : 'font-bold text-destructive'}>
                                                {formatDecimal(record.score, 2, true)}
                                            </span>
                                        </TD>
                                        <TD className='text-center text-muted-foreground'>{formatDecimal(kkm, 2, true)}</TD>
                                        <TD className='text-right print:hidden'>
                                            <Button variant='danger-outline' size='xs' onClick={() => void handleDeleteGrade(record.id)}>
                                                <Trash2 aria-hidden='true' />
                                                Hapus
                                            </Button>
                                        </TD>
                                    </TR>
                                ))}

                                {gradeRows.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className='px-5 py-10 text-center text-sm text-muted-foreground'>
                                            Belum ada catatan nilai pada tanggal ini.
                                        </td>
                                    </tr>
                                )}
                            </TBody>
                        </Table>
                    </Card>
                )}
            </div>
        </>
    )
}

export default DailyReportsPage
